import {
    CalendarState,
    PersonalDate,
    PersonalDateGroup,
    PersonalDatePresentationItem,
    PrintInfo,
    StepGuide,
} from "burde";
import {
    CalendarArticleNames,
    DayModuleElement,
    DefaultCulture,
    DefaultNumberOfSpirals,
    DefaultNumberOfSpiralsList,
    DefaultRubberband,
    DefaultSpiralA5,
    EnhancedEcommerceCalendarNames,
    EnhancedEcommerceNotebookNames,
    NavigationComponent,
    NotebookArticleNames,
    WeekModuleElement,
} from "./constants";
import {
    getDayModulesByIds,
    getWeekModulesByIds,
    getExtraPagesByIds,
} from "./service";
import { getStmMonthDividerById } from "./month-divider-service";
import moment from "moment";
import { CalendarType } from "svg/constants";

const fetchDayModuleElements = async (calendar: CalendarState) => {
    if (calendar.dayModules === null || calendar.dayModules.length === 0) {
        return null;
    }

    const dayModuleIds = calendar.dayModules
        .map((dayModule) => {
            return dayModule.id;
        })
        .filter((v, i, a) => a.indexOf(v) === i);

    const dayModuleOptions = await getDayModulesByIds(
        calendar.culture != null ? calendar.culture : DefaultCulture,
        dayModuleIds,
    );

    if (dayModuleOptions !== null) {
        const dayModuleElements = calendar.dayModules.map(
            (calendarDayModule) => {
                const matchingDayModule = dayModuleOptions.filter(
                    (x) => x.id == calendarDayModule.id,
                );

                return {
                    ...matchingDayModule[0],
                    slotId: calendarDayModule.slotId,
                } as DayModuleElement;
            },
        );

        return dayModuleElements as DayModuleElement[] | null;
    }

    return null;
};

const fetchWeekModuleElements = async (calendar: CalendarState) => {
    if (calendar.weekModules === null || calendar.weekModules.length === 0) {
        return null;
    }

    const weekModuleIds = calendar.weekModules
        .map((weekModule) => {
            return weekModule.id;
        })
        .filter((v, i, a) => a.indexOf(v) === i);

    const weekModuleOptions = await getWeekModulesByIds(
        calendar.culture != null ? calendar.culture : DefaultCulture,
        weekModuleIds,
    );

    if (weekModuleOptions !== null) {
        const weekModuleElements = calendar.weekModules.map(
            (calendarWeekModule) => {
                const matchingWeekModule = weekModuleOptions.filter(
                    (x) => x.id == calendarWeekModule.id,
                );

                return {
                    ...matchingWeekModule[0],
                    slotId: calendarWeekModule.slotId,
                } as WeekModuleElement;
            },
        );

        return weekModuleElements;
    }

    return null;
};

const fetchExtraPageElements = async (calendar: CalendarState) => {
    if (calendar.extraPages === null || calendar.extraPages.length === 0) {
        return null;
    }

    return await getExtraPagesByIds(
        calendar.culture != null ? calendar.culture : DefaultCulture,
        calendar.calendarSize,
        calendar.extraPages,
    );
};

const getCalendarStateFromPrintInfo = (printInfo: PrintInfo) => {
    const calendarState: CalendarState = {
        id: printInfo.calendarId,
        name: printInfo.name,
        startDate: printInfo.startDate,
        weekModules: printInfo.weekModules,
        dayModules: printInfo.dayModules,
        personalDateItems: printInfo.personalDatePresentationItems,
        optionalDaySettings: printInfo.optionalDaySettings,
        thumbnailId: printInfo.thumbnailId,
        culture: printInfo.culture,
        calendarSize: printInfo.calendarSize,
        userId: printInfo.userId,
        layoutId: printInfo.calendarLayout,
        lines: printInfo.lines,
        extraPages: printInfo.extraPages.map((e) => e.extraPageId),
        monthDivider: printInfo.monthDivider,
        ownerPage: printInfo.ownerPage,
        isTheme: printInfo.isTheme,
        calendarType: printInfo.calendarType,
        customNumberOfEndPages: printInfo.customNumberOfEndPages,
        coverImageId:
            printInfo.cover.image !== null ? printInfo.cover.image.id : null,
        customCoverImageId:
            printInfo.cover.customImage !== null
                ? printInfo.cover.customImage.id
                : null,
        coverColor: printInfo.cover.color,
        backCoverImageId:
            printInfo.cover.backImage !== null
                ? printInfo.cover.backImage.id
                : null,
        customBackImageId:
            printInfo.cover.backCustomImage !== null
                ? printInfo.cover.backCustomImage.id
                : null,
        backCoverColor: printInfo.cover.backColor,
        coverTexts: printInfo.cover.texts,
        spiral:
            printInfo.cover.spiral !== null
                ? printInfo.cover.spiral.articleNumber
                : DefaultSpiralA5.articleNumber,
        rubberband:
            printInfo.cover.rubberBand !== null
                ? printInfo.cover.rubberBand.articleNumber
                : DefaultRubberband.articleNumber,
        borderImageId: printInfo.border.borderImageId,
        borderColor: printInfo.border.color,
        fontId: printInfo.fontId,
        fontSetName: printInfo.fontSetName,
    };

    return calendarState;
};

const getPersonalDatesFromGroup = (
    personalDateGroup: PersonalDateGroup | null,
) => {
    if (personalDateGroup == null) {
        return [];
    }

    return personalDateGroup.personalDates.map((personalDate) => {
        // Months apparently get zero-indexed while days and years do not.
        personalDate.month++;
        return getPersonalDatePresentationItemFromPersonalDate(personalDate);
    });
};

const getPersonalDateFromPersonalDatePresentationItem = (
    personalDate: PersonalDatePresentationItem,
) => {
    const date = moment(personalDate.date, "YYYY-MM-DD");

    return {
        icon: personalDate.icon,
        text: personalDate.text,
        year: date.year(),
        month: date.month(),
        day: date.day(),
    } as PersonalDate;
};

const getPersonalDatePresentationItemFromPersonalDate = (
    personalDate: PersonalDate,
) => {
    const day =
        personalDate.day < 10 ? `0${personalDate.day}` : `${personalDate.day}`;
    const month =
        personalDate.month < 10
            ? `0${personalDate.month}`
            : `${personalDate.month}`;
    const year = `${personalDate.year}`;
    const date = moment(`${year}-${month}-${day}`, "YYYY-MM-DD");

    return {
        icon: personalDate.icon,
        text: personalDate.text,
        date: date.format("YYYY-MM-DD"),
    } as PersonalDatePresentationItem;
};

const fetchMonthDividerElement = async (calendar: CalendarState) => {
    if (!calendar.monthDivider || !calendar.monthDivider.layoutId) {
        return null;
    }

    return await getStmMonthDividerById(
        calendar.culture !== null ? calendar.culture : DefaultCulture,
        calendar.calendarSize,
        calendar.monthDivider.layoutId,
    );
};

const getNavigationComponentsFromStepGuide = (
    stepGuide: StepGuide,
    navigationComponents: NavigationComponent[],
) => {
    const currentComponents: NavigationComponent[] = [];

    for (let i = 0; i < stepGuide.steps.length; i++) {
        const component = navigationComponents.find(
            (c) => c.name === stepGuide.steps[i],
        );

        if (component !== undefined) {
            currentComponents.push(component);
        }
    }

    return currentComponents;
};

const validateEmail = (email: string) => {
    const regex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
    return regex.test(email);
};

const getCalendarDataLayerName = (
    calendarSize: string,
    calendarType: CalendarType,
) => {
    const articleName =
        calendarType.toLowerCase() === CalendarType.Calendar
            ? CalendarArticleNames[calendarSize.toUpperCase()]
            : NotebookArticleNames[calendarSize.toUpperCase()];

    return calendarType.toLowerCase() === CalendarType.Calendar.toLowerCase()
        ? EnhancedEcommerceCalendarNames[articleName]
        : EnhancedEcommerceNotebookNames[articleName];
};

const getCalendarDataLayerId = (
    calendarSize: string,
    calendarType: CalendarType,
) => {
    const articleName =
        calendarType.toLowerCase() === CalendarType.Calendar
            ? CalendarArticleNames[calendarSize.toUpperCase()]
            : NotebookArticleNames[calendarSize.toUpperCase()];

    return articleName;
};

const getNumberOfSpiralsFromCalendarSize = (calendarSize: string) => {
    const spiralData = DefaultNumberOfSpiralsList.find(
        (data) =>
            data.calendarSize.toLocaleLowerCase() ===
            calendarSize.toLowerCase(),
    );

    if (spiralData !== undefined) {
        return spiralData.value;
    }

    return DefaultNumberOfSpirals;
};

export {
    fetchDayModuleElements,
    fetchWeekModuleElements,
    getCalendarStateFromPrintInfo,
    fetchExtraPageElements,
    getPersonalDatesFromGroup,
    getPersonalDateFromPersonalDatePresentationItem,
    getPersonalDatePresentationItemFromPersonalDate,
    fetchMonthDividerElement,
    getNavigationComponentsFromStepGuide,
    validateEmail,
    getCalendarDataLayerName,
    getCalendarDataLayerId,
    getNumberOfSpiralsFromCalendarSize,
};
